import type { RuleDefinition } from "../ruleRegistry";

export const HEATER_RULES = [
	{
		id: "heater.standardKwSteps",
		category: "heater",
		label: "Aggregat effektsteg (kW)",
		description:
			"Tillgängliga standardeffekter för bastuaggregat. Systemet väljer det minsta steg vars av tillverkaren angivna rumsvolym (heater.stepMaxVolumeM3) rymmer bastuns effektiva volym.",
		defaultValue: [4.5, 6.0, 8.0, 9.0, 10.5, 12.0, 15.0],
		valueType: "number[]",
		unit: "kW",
	},
	{
		id: "heater.stepMaxVolumeM3",
		category: "heater",
		label: "Största rumsvolym per effektsteg (m³)",
		description:
			"Tillverkarens största rekommenderade bastuvolym för varje effektsteg. Ett aggregat väljs om bastuns effektiva volym ryms inom stegets max. Ersätter tumregeln 1 kW per m³, som överdimensionerar (13 m³ blev 15 kW där tillverkarna anger 9 kW för 8–14 m³).",
		defaultValue: {
			"4.5": 6,
			"6": 8,
			"8": 12,
			"9": 14,
			"10.5": 15,
			"12": 17,
			"15": 28,
		},
		valueType: "json",
		unit: "m³",
		source:
			"Harvia produktsidor (KIP45 2,8–6, KIP60 5–8, KIP80 7–12, KIP90E 8–14, Vega Pro BC105 9–15, Hidden Heater HH12 12–17, Club K15 17–28 m³), läst 2026-10-01; Narvi NM 4,5/6/9 kW anger samma intervall.",
		dangerLevel: "critical",
	},
	{
		id: "heater.coldSurfaceFactor",
		category: "heater",
		label: "Kall-yte-tillägg",
		description:
			"Fiktiv volym som adderas per m² glas/sten för att kompensera värmeförlust. Formel: effektiv volym = grundvolym + (glasarea + stenarea) × detta värde.",
		defaultValue: 1.2,
		valueType: "number",
		unit: "+ m³ per m²",
		source: "Harvia / Tylö monteringsanvisningar",
	},
	{
		id: "heater.panorama.heatLossMultiplier",
		category: "heater",
		label: "Panoramafönster värmeförlust",
		description:
			"Multiplikator på glaspåslaget när en two-room-design använder panoramaglas. Håller standardfönster oförändrade men gör fullväggsglas mer konservativt.",
		defaultValue: 1.4,
		valueType: "number",
		unit: "×",
		source: "Konservativ produktkalibrering tills leverantörsdata finns",
	},
	{
		id: "heater.logCabinFactor",
		category: "heater",
		label: "Utomhusstugfaktor",
		description:
			"Multiplikator för effektiv volym vid ouppvärmda utomhusstugor.",
		defaultValue: 1.5,
		valueType: "number",
		unit: "×",
	},
	{
		id: "heater.voltageSystem",
		category: "heater",
		label: "Spänningssystem",
		description: "Elanslutningens spänning och fas.",
		defaultValue: "400V 3-fas",
		valueType: "string",
		source: "SS 436 40 00",
	},
	{
		id: "heater.fuseThresholdAmps",
		category: "heater",
		label: "Säkringsgräns (A)",
		description:
			"Ampere per fas upp till vilken 10A-säkring räcker. Däröver väljs minsta standardsäkring (16/20/25A) som klarar strömmen per fas.",
		defaultValue: 10,
		valueType: "number",
		unit: "A",
	},
	{
		id: "heater.upfuseWarningThreshold",
		category: "heater",
		label: "Uppsäkringsgräns (A)",
		description: "Säkringsvärde ovanför vilket varning visas om uppsäkring.",
		defaultValue: 16,
		valueType: "number",
		unit: "A",
	},
	{
		id: "heater.clearance.wood.sideMm",
		category: "heater",
		label: "Skyddsavstånd ved – sida (mm)",
		description:
			"Minsta horisontella avstånd i sidled (X-led) från vedeldat aggregat till brännbart material. Standardvärdet förutsätter strålskydd (gnistskydd/plåt) enligt monteringsanvisning — oskyddat krävs typiskt 500 mm.",
		defaultValue: 150,
		valueType: "number",
		unit: "mm",
		source: "BBR 5:4 + Harvia/Narvi monteringsanvisning (med strålskydd)",
		dangerLevel: "critical",
	},
	{
		id: "heater.clearance.wood.frontMm",
		category: "heater",
		label: "Skyddsavstånd ved – front (mm)",
		description:
			"Minsta horisontella avstånd i djupled (Z-led) från vedeldat aggregat till brännbart material. Standardvärdet förutsätter strålskydd — oskyddat krävs typiskt 500 mm.",
		defaultValue: 120,
		valueType: "number",
		unit: "mm",
		source: "BBR 5:4 + Harvia/Narvi monteringsanvisning (med strålskydd)",
		dangerLevel: "critical",
	},
	{
		id: "heater.clearance.wood.ceilingMm",
		category: "heater",
		label: "Skyddsavstånd ved – tak (mm)",
		description:
			"Minsta avstånd från vedeldat aggregats översta stenar till innertak.",
		defaultValue: 1200,
		valueType: "number",
		unit: "mm",
		source: "BBR 5:4 + Harvia monteringsanvisning",
		dangerLevel: "critical",
	},
	{
		id: "heater.clearance.electric.sideMm",
		category: "heater",
		label: "Skyddsavstånd el – sida (mm)",
		description:
			"Minsta horisontella avstånd i sidled (X-led) från elaggregat till brännbart material.",
		defaultValue: 100,
		valueType: "number",
		unit: "mm",
		source: "Tylö/Harvia monteringsanvisning",
		dangerLevel: "critical",
	},
	{
		id: "heater.clearance.electric.frontMm",
		category: "heater",
		label: "Skyddsavstånd el – front (mm)",
		description:
			"Minsta horisontella avstånd i djupled (Z-led) från elaggregat till brännbart material.",
		defaultValue: 100,
		valueType: "number",
		unit: "mm",
		source: "Tylö/Harvia monteringsanvisning",
		dangerLevel: "critical",
	},
	{
		id: "heater.clearance.electric.ceilingMm",
		category: "heater",
		label: "Skyddsavstånd el – tak (mm)",
		description:
			"Minsta avstånd från elaggregatets översta stenar till innertak.",
		defaultValue: 1100,
		valueType: "number",
		unit: "mm",
		source: "Tylö/Harvia monteringsanvisning",
		dangerLevel: "critical",
	},
	{
		id: "heater.glassPenaltyWarningText",
		category: "heater",
		label: "Glaspåslag varningstext",
		description:
			"Varningstext som visas när fönstren läcker för mycket värme. {kw} ersätts med beräknat kW-behov.",
		defaultValue:
			"Ditt fönster läcker för mycket värme. Byt till ett {kw} kW aggregat eller gör fönstret mindre.",
		valueType: "string",
		dangerLevel: "warning",
	},
] as const satisfies readonly RuleDefinition[];
